import React, { useState } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  ListItemText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import styled from "@emotion/styled";
import CustomTooltip from "../Tooltip/Tooltip";

const StyledMenuPaper = styled("div")({
  "&::-webkit-scrollbar": {
    width: "10px",
    height: "10px",
  },
  "&::-webkit-scrollbar-track": {
    borderRadius: "5px",
    backgroundColor: "#e6f1f5",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "5px",
    backgroundColor: "#1b52a729",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#1b52a742",
  },
  "&::-webkit-scrollbar-thumb:active": {
    backgroundColor: "#1b52a740",
  },
});

export const StyledFormControl = styled(FormControl)({
  position: "relative",
});

export const StyledSelect = styled(Select)({
  "& .MuiSelect-icon": {
    color: "var(--header__color)",
    right: "4px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "12px",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderRadius: "12px",
    border: "2px solid #6860ee",
  },
  "& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input": {
    padding: "10px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderRadius: "12px",
    border: "2px solid rgb(142, 136, 244)",
  },
  "&.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--border__color)",
  },
  "& .MuiSelect-select": {
    padding: "10px",
    paddingLeft: "14px",
    fontSize: "15px",
    outline: "none",
    background: "none",
    color: "var(--header__color)",
    borderRadius: "12px !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "12px",
    border: "1px solid var(--border__color)",
  },
});

export const StyledMenuItem = styled(MenuItem)({
  position: "relative",
  backgroundColor: "var(--body__background)",
  color: "var(--selectinput_dropdown_text)",
  "&.Mui-selected": {
    backgroundColor: "var(--tab__background)",
    color: "#6860ee",
  },
  "&.Mui-selected:hover": {
    backgroundColor: "var(--body__background)",
    color: "var(--header__color)",
  },
  "&:hover": {
    color: "var(--header__color)",
    backgroundColor: "var(--tab__background)",
  },
});

export const StyledErrorMessage = styled("div")({
  color: "red",
  fontSize: "15px",
  marginTop: "6px",
});

export const StyledCheckIcon = styled(CheckIcon)({
  marginLeft: "auto",
  color: "#6860ee",
  display: "block",
});

export const StyledClearButton = styled("button")({
  position: "absolute",
  top: "50%",
  right: "34px",
  zIndex: 2,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "22px",
  height: "22px",
  padding: 0,
  border: 0,
  borderRadius: "50%",
  background: "transparent",
  color: "var(--header__color)",
  cursor: "pointer",
  transform: "translateY(-50%)",
  "&:hover": {
    background: "rgba(104, 96, 238, 0.12)",
    color: "#6860ee",
  },
});

export const StyledClearIcon = styled(CloseIcon)({
  fontSize: "18px",
});

const ColorIndicator = styled("span")(({ color }) => ({
  display: "inline-block",
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  backgroundColor: color || "transparent",
  marginRight: "10px",
}));

const TIMEZONE_ALIASES = {
  "Asia/Calcutta": "Asia/Kolkata",
  "Etc/GMT+0": "Etc/UTC",
  "Etc/GMT-0": "Etc/UTC",
  "Etc/GMT0": "Etc/UTC",
  "Etc/Greenwich": "Etc/UTC",
  "Etc/UCT": "Etc/UTC",
  "Etc/Universal": "Etc/UTC",
  "Etc/Zulu": "Etc/UTC",
};

const DynamicSelectInput = ({
  options = [],
  onChange = () => {},
  value = "",
  name = "",
  defaultOption = "Select",
  label = "",
  required = false,
  error = false,
  errorMessage = "",
  labelKey = "title",
  selectStyle = {
    fontFamily: "Satoshi",
    fontSize: "15px",
    fontWeight: 500,
    color: "var(--header__color)",
    borderRadius: "12px",
  },
  wrapperStyle = {},
  selectSx = {},
  labelStyle = {},
  menuWidth = 300,
  onBlur = () => {},
  enableTooltip = false,
  menuProps = {},
  menuZIndex = 1500,
  clearable = false,
  disabled = false,
  defaultOptionStyle = {},
  disableDefaultOption = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (event) => {
    setIsFocused(false);
    onBlur(event);
  };
  const handleOpen = () => setIsOpen(true);
  const handleClose = () => {
    setIsOpen(false);
    onBlur({ target: { name } });
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onChange({ target: { name, value: "" } });
  };

  const normalizedValue = TIMEZONE_ALIASES[value] || value;
  const safeOptions = Array.isArray(options) ? options : [];
  const isValidValue =
    !normalizedValue ||
    safeOptions.some(
      (option) => (option.value ?? option[labelKey]) === normalizedValue
    );
  const displayValue = isValidValue ? normalizedValue : "";

  const renderValue = (selected) => {
    if (!selected) {
      return <span style={defaultOptionStyle}>{defaultOption}</span>;
    }
    const selectedOption = safeOptions.find(
      (option) => (option.value ?? option[labelKey]) === selected
    );
    const displayText = selectedOption ? selectedOption[labelKey] : selected;
    return enableTooltip ? (
      <CustomTooltip title={displayText}>{displayText}</CustomTooltip>
    ) : (
      displayText
    );
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;

  const MenuProps = {
    sx: {
      zIndex: menuZIndex,
    },
    PaperProps: {
      component: StyledMenuPaper,
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: menuWidth,
        // backgroundColor: "white",
        borderRadius: "8px",
        border: "0.5px solid var(--border__color)",
      },
    },
    disableScrollLock: true,
    ...menuProps,
  };
  
  const resolvedSelectSx = clearable
    ? {
        ...selectSx,
        "& .MuiSelect-select": {
          ...(selectSx?.["& .MuiSelect-select"] || {}),
          paddingRight: "64px !important",
        },
      }
    : selectSx;

  return (
    <StyledFormControl fullWidth error={error} style={wrapperStyle}>
      {label && (
        <label style={labelStyle}>
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </label>
      )}

      <StyledSelect
        sx={resolvedSelectSx}
        name={name}
        value={displayValue}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onOpen={handleOpen}
        onClose={handleClose}
        style={selectStyle}
        MenuProps={MenuProps}
        displayEmpty
        renderValue={renderValue}
        IconComponent={ExpandMoreIcon}
        disabled={disabled}
      >
        {!disableDefaultOption && (
          <StyledMenuItem value="">
            <em>{defaultOption}</em> 
          </StyledMenuItem>
        )}

        {safeOptions.map((option, index) => {
          const optionValue = option.value ?? option[labelKey];
          const labelText = option[labelKey];

          return (
            <StyledMenuItem key={index} value={optionValue}>
              {option.colorIndicator && (
                <ColorIndicator color={option.colorIndicator} />
              )}

              {enableTooltip ? (
                <CustomTooltip 
                title={labelText}
                placement="right" arrow
                >
                  <ListItemText 
                  primary={labelText} 
                  className="listitem-text"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                  />
                </CustomTooltip>
              ) : (
                <ListItemText primary={labelText} className="listitem-text" />
              )}
              {isOpen && displayValue === optionValue && <StyledCheckIcon />}
            </StyledMenuItem>
          );
        })}
      </StyledSelect>
      {clearable && displayValue && (
        <StyledClearButton
          type="button"
          aria-label="Clear selected option"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={handleClear}
        >
          <StyledClearIcon />
        </StyledClearButton>
      )}
      {error && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
    </StyledFormControl>
  );
};

export default DynamicSelectInput;